import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      // 401 means unauthenticated - expected on first load
      if (e?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === "development") console.warn("[auth] refresh failed", e);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /auth/me probe on fully public routes to avoid noisy 401s
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      if (p.startsWith("/ticket-status/")) {
        setUser(false);
        setLoading(false);
        return;
      }
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === "development") console.warn("[auth] logout failed", e);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function landingForRole(role) {
  switch (role) {
    case "SUPER_ADMIN":
    case "AGENCY_ADMIN":
    case "AUDITOR":
      return "/admin";
    case "RECEPTIONIST":
      return "/kiosk";
    case "COUNTER_AGENT":
      return "/counter";
    case "DISPLAY_ONLY":
      return "/display";
    default:
      return "/login";
  }
}
